import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { fetchSearchConsoleQuery } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search as SearchIcon, Loader2, Globe, AlertCircle, Download } from "lucide-react";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;
const fmtPos = (n) => (n == null ? "—" : Number(n).toFixed(1));

export default function GscTopQueriesCard({ defaultSiteUrl = "" }) {
  const [siteUrl, setSiteUrl] = useState(defaultSiteUrl);
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);

  const mut = useMutation({
    mutationFn: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 28);
      return fetchSearchConsoleQuery({
        site_url: siteUrl.trim(),
        start_date: start.toISOString().slice(0, 10),
        end_date: end.toISOString().slice(0, 10),
        dimensions: ["query"],
        row_limit: 200,
      });
    },
    onSuccess: (data) => {
      const r = (data?.rows || []).map((row) => ({
        query: row.keys?.[0] || "",
        clicks: row.clicks || 0,
        impressions: row.impressions || 0,
        ctr: row.ctr || 0,
        position: row.position || 0,
      }));
      setRows(r);
    },
  });

  const filtered = filter.trim()
    ? rows.filter((r) => r.query.toLowerCase().includes(filter.toLowerCase()))
    : rows;

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="gsc-top-queries-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3 flex-wrap">
        <SearchIcon className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Top queries reales · Search Console</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Qué buscaron los usuarios antes de hacer clic en tu sitio · últimos 28 días</p>
        </div>
      </div>

      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <Globe className="h-3 w-3" /> Site URL
            </label>
            <Input
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="sc-domain:tudominio.com o https://tudominio.com/"
              className="h-9 text-xs font-mono"
              data-testid="gsc-tq-site-url"
            />
          </div>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !siteUrl.trim()}
            className="h-9"
            data-testid="gsc-tq-load-btn"
          >
            {mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Cargar
          </Button>
          {rows.length > 0 && (
            <div className="relative w-full sm:w-56">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Filtrar queries…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-9 pl-9 bg-white text-xs"
                data-testid="gsc-tq-filter"
              />
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-500 mt-2">
          Para que aparezca, <span className="font-mono bg-slate-100 px-1 rounded">narwhal-dashboard@dashboards-emergent.iam.gserviceaccount.com</span> debe estar como usuario en Search Console.
        </p>
      </div>

      {mut.error && (
        <div className="px-5 py-4 bg-rose-50 border-b border-rose-100 text-xs text-rose-800 flex items-start gap-2" data-testid="gsc-tq-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">No se pudo cargar Search Console</div>
            <div className="mt-0.5">{mut.error?.response?.data?.detail || mut.error?.message}</div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-400" data-testid="gsc-tq-empty">
          Ingresá el site URL y apretá <span className="font-semibold">Cargar</span> para ver las queries reales.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-5 py-2 font-semibold">Query</th>
                <th className="px-2 py-2 font-semibold text-right">Clicks</th>
                <th className="px-2 py-2 font-semibold text-right">Impresiones</th>
                <th className="px-2 py-2 font-semibold text-right">CTR</th>
                <th className="px-5 py-2 font-semibold text-right">Posición prom.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.query} className="hover:bg-slate-50">
                  <td className="px-5 py-2 font-medium text-slate-800">{r.query}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{fmtNum(r.clicks)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtNum(r.impressions)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtPct(r.ctr)}</td>
                  <td className="px-5 py-2 text-right font-mono tabular-nums font-semibold text-indigo-700">{fmtPos(r.position)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-[11px] text-slate-400 italic">Sin queries que matcheen el filtro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
